import { useMemo, useState } from "react";
import LoaderPage from "src/components/Loader/LoaderPage";
import PaginatedDiv from "src/components/PaginatedDiv";
import usePagination from "src/hooks/usePagination";
import { getTalentsPaginated } from "src/network/talent";
import { TalentPaginatedParams } from "src/network/talent/type";
import Layout from "src/router/AuthenticatedRouter/Layout";
import { Talent } from "src/types";
import TalentCard from "./components/TalentCard";
import Filters from "./components/Filters/Filters";
import { WORK_HOURS_PERIOD } from "src/constants";
import Empty from "../../components/EmptyPagination/Empty";
import { FiltersValue } from "./components/Filters/types";
import { defaultFilters } from "./components/Filters/constant";

const TalentsPage = () => {
  const [filters, setFilters] = useState(defaultFilters());
  return (
    <Layout title="Talent" rightChildren={<Filters value={filters} setValue={setFilters} />}>
      <Talents _filters={filters} />
    </Layout>
  );
};

const Talents = ({ _filters }: { _filters: FiltersValue }) => {
  // Transalte local filters to API filters
  const filters: TalentPaginatedParams = useMemo(() => {
    const newFilters: TalentPaginatedParams = { sort_by: { name: "company_base" } };
    const {
      keyword,
      experience_level,
      skills,
      resume_skills,
      salary,
      countries,
      state,
      role,
      sort_by_job,
      vetted
    } = _filters;
    const salary_min = salary?.salary?.[0];
    const salary_max = salary?.salary?.[1];
    // We manually convert the salary to hourly rate based on the period
    const X = WORK_HOURS_PERIOD[salary.period];
    if (vetted) newFilters.is_trusted_talent = "TRUSTED";
    if (keyword) newFilters.keyword = keyword;
    if (experience_level) newFilters.seniority_level = [experience_level.value];
    if (skills?.length) newFilters.skills = skills.map((s) => s.value);
    if (resume_skills?.length) newFilters.resume_skills = resume_skills.map((s) => s.value);
    if (salary_min) newFilters.min_hourly_rate = salary_min / X;
    if (salary_max) newFilters.max_hourly_rate = salary_max / X;
    if (countries?.length) newFilters.country = countries.map((l) => l.value);
    if (state) newFilters.city = [state.value];
    if (role) newFilters.role = role.value;
    if (sort_by_job)
      newFilters.sort_by = { name: "company_job", job_id: Number(sort_by_job.value) };
    return newFilters;
  }, [_filters]);
  const { data, count, fetching, fetch } = usePagination<Talent, TalentPaginatedParams>({
    fetchPage: getTalentsPaginated,
    page_size: 16,
    filters
  });

  if (!data) return <LoaderPage />;
  if (count === 0) return <Empty className="mt-[100px]" title="There are no talents available" />;

  return (
    <PaginatedDiv onLimit={fetch} loading={fetching} count={count} currentCount={data?.length}>
      <div className="w-full max-w-[1200px] mx-auto grid grid-cols-2 pr-4 gap-6 pb-10">
        {data.map((t) => (
          <TalentCard key={t.freelancer_id} talent={t} />
        ))}
      </div>
    </PaginatedDiv>
  );
};

export default TalentsPage;
